import React from "react";
import {
  Paper,
  Box,
  TextField,
  Button,
  Chip,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  SelectAll as SelectAllIcon,
} from "@mui/icons-material";
import { RentalObject } from "../../types/api-types";

interface ObjectSelectionBarProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  selectedObjects: Set<string>;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onAddBundle: () => void;
  filteredObjects: RentalObject[];
  totalObjects: number;
}

const ObjectSelectionBar: React.FC<ObjectSelectionBarProps> = ({
  searchTerm,
  onSearchChange,
  selectedObjects,
  onSelectAll,
  onClearSelection,
  onAddBundle,
  filteredObjects,
  totalObjects,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const selectedCount = selectedObjects.size;
  const isAllSelected =
    selectedCount === filteredObjects.length && filteredObjects.length > 0;

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, md: 3 },
        mb: { xs: 2, md: 3 },
        borderRadius: { xs: 2, md: 3 },
        mx: { xs: 0.5, md: 0 },
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        border: "1px solid rgba(0,0,0,0.06)",
        background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
        position: { xs: "sticky", md: "static" },
        top: { xs: 0, md: "auto" },
        zIndex: { xs: 10, md: "auto" },
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        {/* Search */}
        <TextField
          placeholder="Objekte durchsuchen..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            minWidth: { xs: "100%", md: 250 },
            "& .MuiOutlinedInput-root": {
              borderRadius: { xs: 2, md: 3 },
              bgcolor: "white",
              fontSize: { xs: "0.9rem", md: "1rem" },
              "& fieldset": {
                borderColor: "rgba(0,0,0,0.12)",
                borderWidth: "1.5px",
              },
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
              "&.Mui-focused fieldset": {
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  color="primary"
                  sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
                />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={() => onSearchChange("")}
                  sx={{
                    minWidth: "auto",
                    p: 0.5,
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </Button>
              </InputAdornment>
            ),
          }}
        />

        {/* Selection Info */}
        <Box
          display="flex"
          alignItems="center"
          gap={{ xs: 1, md: 1.5 }}
          flexWrap="wrap"
          sx={{ minWidth: "fit-content" }}
        >
          {selectedCount > 0 && (
            <Chip
              label={`${selectedCount} ausgewählt`}
              color="primary"
              variant="filled"
              size="small"
              onDelete={onClearSelection}
              deleteIcon={<ClearIcon />}
              sx={{
                fontSize: { xs: "0.75rem", md: "0.8rem" },
                height: { xs: 24, md: 28 },
                fontWeight: 600,
                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
              }}
            />
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              whiteSpace: "nowrap",
              fontSize: { xs: "0.8rem", md: "0.875rem" },
              fontWeight: 500,
            }}
          >
            {filteredObjects.length} von {totalObjects} Objekten
          </Typography>
        </Box>

        {/* Actions */}
        <Box display="flex" gap={{ xs: 1, md: 1.5 }} sx={{ flexShrink: 0 }}>
          {/* Select All */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                indeterminate={selectedCount > 0 && !isAllSelected}
                onChange={onSelectAll}
                size="small"
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: "1.2rem", md: "1.4rem" },
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  fontWeight: 500,
                }}
              >
                {isMobile ? "Alle" : "Alle auswählen"}
              </Typography>
            }
            sx={{
              mr: { xs: 0.5, md: 1 },
              bgcolor: "rgba(25, 118, 210, 0.04)",
              borderRadius: 1.5,
              px: 1,
              py: 0.25,
              border: "1px solid rgba(25, 118, 210, 0.2)",
            }}
          />

          {/* Add Bundle Button */}
          <Button
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            onClick={onAddBundle}
            disabled={selectedCount === 0}
            size={isMobile ? "small" : "medium"}
            sx={{
              minWidth: { xs: "auto", md: 150 },
              px: { xs: 2, md: 3 },
              py: { xs: 0.75, md: 1 },
              fontSize: { xs: "0.8rem", md: "0.9rem" },
              fontWeight: 600,
              borderRadius: { xs: 1.5, md: 2 },
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
              },
              "&:disabled": {
                opacity: 0.6,
                transform: "none",
                boxShadow: "none",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {isMobile ? "Bundle" : "Bundle hinzufügen"}
          </Button>
        </Box>
      </Box>

      {/* Selection Summary */}
      {selectedCount > 0 && (
        <Box
          mt={2}
          p={1.5}
          sx={{
            backgroundColor: "primary.light",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.main",
          }}
        >
          <Typography variant="body2" color="primary.dark">
            <strong>{selectedCount}</strong>{" "}
            {selectedCount === 1 ? "Objekt" : "Objekte"} für Bundle-Erstellung
            ausgewählt
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ObjectSelectionBar;
